// Admin Beta Testing — upload signed APK/AAB, list testers, track install
// status and print test results per tester.
import { useEffect, useRef, useState } from "react";
import api, { extractError } from "@/api/client";
import { toast } from "sonner";
import {
  X, Upload, Package, Trash2, Users, PlusCircle, Copy, ExternalLink,
  CheckCircle2, XCircle, Clock, MessageSquare, Smartphone,
} from "lucide-react";

const STATUS_LABEL = {
  sent: { label: "أُرسل", color: "bg-amber-100 text-amber-700" },
  installed: { label: "مثبَّت", color: "bg-emerald-100 text-emerald-700" },
  failed: { label: "فشل التثبيت", color: "bg-red-100 text-red-700" },
};
const PRINT_LABEL = {
  pending: { label: "قيد الاختبار", icon: Clock, color: "text-amber-600" },
  pass: { label: "طباعة سليمة", icon: CheckCircle2, color: "text-emerald-600" },
  fail: { label: "طباعة معطلة", icon: XCircle, color: "text-red-600" },
};

export default function AdminBetaTestersModal({ onClose }) {
  const [tab, setTab] = useState("testers"); // testers | apks
  const [apks, setApks] = useState([]);
  const [testers, setTesters] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setErr("");
    try {
      const [a, t] = await Promise.all([
        api.get("/admin/beta/apks"),
        api.get("/admin/beta/testers"),
      ]);
      setApks(a.data.apks || []);
      setTesters(t.data.testers || []);
    } catch (e) { setErr(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { setErr("اختر ملف .apk أو .aab"); return; }
    const version = window.prompt("رقم الإصدار (مثال 1.0.0):", "1.0.0");
    if (!version) return;
    const build = window.prompt("رقم البناء (build number):", "1");
    if (!build) return;
    const notes = window.prompt("ملاحظات (اختياري):", "") || "";
    const fd = new FormData();
    fd.append("file", f);
    fd.append("version", version);
    fd.append("build", build);
    fd.append("notes", notes);
    setBusy(true); setErr("");
    try {
      await api.post("/admin/beta/apks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("✓ تم رفع الإصدار", { duration: 2500 });
      fileRef.current.value = "";
      await load();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const deleteApk = async (id) => {
    if (!window.confirm("حذف هذا الإصدار من القائمة؟")) return;
    setBusy(true);
    try { await api.delete(`/admin/beta/apks/${id}`); await load(); }
    catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const copyDownload = async (apkId) => {
    const url = `${window.location.origin}/api/beta/download/${apkId}`;
    try { await navigator.clipboard.writeText(url); toast.success("✓ تم نسخ رابط التحميل", { duration: 2000 }); }
    catch { window.prompt("انسخ الرابط:", url); }
  };

  const addTester = async (payload) => {
    setBusy(true); setErr("");
    try {
      await api.post("/admin/beta/testers", payload);
      toast.success("✓ تمت الإضافة", { duration: 2000 });
      setShowAdd(false);
      await load();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const patchTester = async (id, patch) => {
    try {
      await api.patch(`/admin/beta/testers/${id}`, patch);
      await load();
    } catch (e) { setErr(extractError(e)); }
  };

  const removeTester = async (id, name) => {
    if (!window.confirm(`إزالة ${name} من قائمة المُختبرين؟`)) return;
    try { await api.delete(`/admin/beta/testers/${id}`); await load(); }
    catch (e) { setErr(extractError(e)); }
  };

  const summary = {
    total: testers.length,
    installed: testers.filter((t) => t.install_status === "installed").length,
    printPass: testers.filter((t) => t.print_test_result === "pass").length,
    printFail: testers.filter((t) => t.print_test_result === "fail").length,
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="beta-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-3xl rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><Package size={18}/></div>
            <div>
              <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>اختبار APK التجريبي</h2>
              <div className="text-[11px] text-[var(--text-secondary)]">وزّع الإصدار على 3 مطاعم لاختبار الطباعة على أندرويد 8</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center" data-testid="beta-close">
            <X size={16}/>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-4 flex gap-1 bg-white">
          <button onClick={() => setTab("testers")}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 ${tab === "testers" ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)] text-[var(--text-secondary)]"}`}
            data-testid="tab-testers">
            <Users size={14}/> المُختبرون ({testers.length})
          </button>
          <button onClick={() => setTab("apks")}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 ${tab === "apks" ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)] text-[var(--text-secondary)]"}`}
            data-testid="tab-apks">
            <Package size={14}/> الإصدارات ({apks.length})
          </button>
        </div>

        {err && <div className="mx-5 mt-3 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm" data-testid="beta-err">{err}</div>}

        {/* APK TAB */}
        {tab === "apks" && (
          <div className="p-5 space-y-4">
            <div className="rounded-2xl border border-dashed border-primary-brand bg-primary-light/50 p-4">
              <div className="font-black text-sm mb-2 flex items-center gap-2">
                <Upload size={16} className="text-primary-brand"/> ارفع إصداراً جديداً
              </div>
              <p className="text-[11px] text-[var(--text-secondary)] mb-3">
                ابنِ APK موقّعاً من Android Studio ثم ارفعه هنا. الحد الأقصى 60 ميغابايت.
              </p>
              <input ref={fileRef} type="file" accept=".apk,.aab"
                className="block w-full text-xs mb-3 file:ml-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-primary-brand file:text-white file:font-bold"
                data-testid="apk-file-input"/>
              <button onClick={upload} disabled={busy}
                className="w-full py-2.5 rounded-xl bg-primary-brand text-white font-black text-sm hover:opacity-90 disabled:opacity-60"
                data-testid="btn-upload-apk">
                {busy ? "جارٍ الرفع..." : "رفع الملف"}
              </button>
            </div>

            {apks.length === 0 ? (
              <div className="text-center py-10 text-sm text-[var(--text-secondary)]">لا توجد إصدارات مرفوعة بعد</div>
            ) : (
              <div className="space-y-2">
                {apks.map((a) => (
                  <div key={a.id} className="border border-[var(--border)] rounded-2xl p-3" data-testid={`apk-row-${a.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-black text-sm flex items-center gap-2">
                          <span>v{a.version}</span>
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-black">build {a.build}</span>
                        </div>
                        <div className="text-[11px] text-[var(--text-secondary)] truncate">
                          {a.filename} · {(a.size_bytes / 1024 / 1024).toFixed(1)} MB · {new Date(a.created_at).toLocaleDateString("ar")}
                        </div>
                        {a.notes && <div className="text-xs text-[var(--text-primary)] mt-1">{a.notes}</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={`/api/beta/download/${a.id}`} target="_blank" rel="noopener noreferrer"
                          title="تحميل" className="w-8 h-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 flex items-center justify-center"
                          data-testid={`apk-download-${a.id}`}>
                          <ExternalLink size={14}/>
                        </a>
                        <button onClick={() => copyDownload(a.id)} title="نسخ الرابط"
                          className="w-8 h-8 rounded-lg bg-primary-light hover:bg-blue-100 text-primary-brand flex items-center justify-center"
                          data-testid={`apk-copy-${a.id}`}>
                          <Copy size={14}/>
                        </button>
                        <button onClick={() => deleteApk(a.id)} title="حذف"
                          className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"
                          data-testid={`apk-delete-${a.id}`}>
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TESTERS TAB */}
        {tab === "testers" && (
          <div className="p-5 space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-2">
              <SummaryStat label="إجمالي" value={summary.total} tone="brand"/>
              <SummaryStat label="مثبَّت" value={summary.installed} tone="emerald"/>
              <SummaryStat label="طباعة سليمة" value={summary.printPass} tone="emerald"/>
              <SummaryStat label="طباعة معطلة" value={summary.printFail} tone="red"/>
            </div>

            <button onClick={() => setShowAdd(true)}
              className="w-full py-2.5 rounded-xl bg-primary-brand text-white font-black text-sm hover:opacity-90 flex items-center justify-center gap-2"
              data-testid="btn-add-tester">
              <PlusCircle size={16}/> إضافة مُختبر جديد
            </button>

            {testers.length === 0 ? (
              <div className="text-center py-10 text-sm text-[var(--text-secondary)]">
                لم تُضِف أي مُختبرين بعد. الهدف: 3 مطاعم تعمل على أندرويد 8.
              </div>
            ) : (
              <div className="space-y-2">
                {testers.map((t) => <TesterRow key={t.id} t={t} onPatch={patchTester} onRemove={removeTester}/>)}
              </div>
            )}
          </div>
        )}

        {showAdd && <AddTesterModal busy={busy} onClose={() => setShowAdd(false)} onSubmit={addTester}/>}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, tone }) {
  const bg = { brand: "bg-primary-light text-primary-brand", emerald: "bg-emerald-50 text-emerald-700", red: "bg-red-50 text-red-600" }[tone];
  return (
    <div className={`rounded-xl p-3 text-center ${bg}`}>
      <div className="text-[10px] font-bold">{label}</div>
      <div className="text-2xl font-black" style={{fontFamily:"Cairo"}}>{value}</div>
    </div>
  );
}

function TesterRow({ t, onPatch, onRemove }) {
  const statusMeta = STATUS_LABEL[t.install_status] || STATUS_LABEL.sent;
  const printMeta = PRINT_LABEL[t.print_test_result] || PRINT_LABEL.pending;
  const PrintIcon = printMeta.icon;
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedback, setFeedback] = useState(t.feedback || "");

  return (
    <div className="border border-[var(--border)] rounded-2xl p-3" data-testid={`tester-row-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-black text-sm truncate">{t.name}</div>
          <div className="text-[11px] text-[var(--text-secondary)] truncate">
            {t.restaurant_name && <span>{t.restaurant_name} · </span>}
            {t.phone && <span dir="ltr">{t.phone}</span>}
          </div>
          {(t.device_model || t.android_version) && (
            <div className="text-[10px] text-[var(--text-secondary)] mt-1 flex items-center gap-1">
              <Smartphone size={10}/> {t.device_model || "—"} · Android {t.android_version || "?"}
            </div>
          )}
        </div>
        <button onClick={() => onRemove(t.id, t.name)}
          className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center shrink-0"
          data-testid={`tester-remove-${t.id}`}>
          <Trash2 size={14}/>
        </button>
      </div>

      {/* Status badges */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-black ${statusMeta.color}`}>{statusMeta.label}</span>
        <span className={`text-[10px] flex items-center gap-1 font-black ${printMeta.color}`}>
          <PrintIcon size={11}/> {printMeta.label}
        </span>
      </div>

      {/* Quick controls */}
      <div className="mt-2 grid grid-cols-2 gap-2">
        <select value={t.install_status || "sent"}
          onChange={(e) => onPatch(t.id, { install_status: e.target.value })}
          className="text-xs px-2 py-1.5 rounded-lg border border-[var(--border)] bg-white"
          data-testid={`tester-status-${t.id}`}>
          <option value="sent">أُرسل</option>
          <option value="installed">مثبَّت</option>
          <option value="failed">فشل التثبيت</option>
        </select>
        <select value={t.print_test_result || "pending"}
          onChange={(e) => onPatch(t.id, { print_test_result: e.target.value })}
          className="text-xs px-2 py-1.5 rounded-lg border border-[var(--border)] bg-white"
          data-testid={`tester-print-${t.id}`}>
          <option value="pending">قيد الاختبار</option>
          <option value="pass">طباعة سليمة</option>
          <option value="fail">طباعة معطلة</option>
        </select>
      </div>

      {/* Feedback */}
      <button onClick={() => setOpenFeedback((v) => !v)}
        className="mt-2 w-full py-1.5 rounded-lg bg-[var(--surface-alt)] hover:bg-blue-50 text-xs font-bold flex items-center justify-center gap-1.5"
        data-testid={`tester-fb-toggle-${t.id}`}>
        <MessageSquare size={12}/> {t.feedback ? "تعديل الملاحظات" : "إضافة ملاحظات"}
      </button>
      {openFeedback && (
        <div className="mt-2 space-y-2">
          <textarea rows={2} value={feedback} onChange={(e) => setFeedback(e.target.value)}
            className="w-full text-xs px-2 py-1.5 rounded-lg border border-[var(--border)] bg-white"
            placeholder="ملاحظات المُختبر..." data-testid={`tester-fb-input-${t.id}`}/>
          <button onClick={() => { onPatch(t.id, { feedback }); setOpenFeedback(false); toast.success("✓ حُفظت الملاحظات"); }}
            className="w-full py-1.5 rounded-lg bg-primary-brand text-white text-xs font-black hover:opacity-90"
            data-testid={`tester-fb-save-${t.id}`}>
            حفظ
          </button>
        </div>
      )}
      {!openFeedback && t.feedback && (
        <div className="mt-2 text-[11px] bg-[var(--surface-alt)] rounded-lg p-2">{t.feedback}</div>
      )}
    </div>
  );
}

function AddTesterModal({ busy, onClose, onSubmit }) {
  const [f, setF] = useState({ name: "", phone: "", restaurant_name: "", device_model: "", android_version: "", notes: "" });
  const disabled = !f.name.trim() || busy;
  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="add-tester-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 talabya-shadow-float">
        <div className="flex items-center justify-between mb-4">
          <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>إضافة مُختبر</div>
          <button onClick={onClose} className="text-[var(--text-secondary)] p-1"><X size={16}/></button>
        </div>
        <div className="space-y-2">
          <FieldInput label="الاسم" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="add-tester-name" required/>
          <FieldInput label="الهاتف" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="add-tester-phone"/>
          <FieldInput label="اسم المطعم" value={f.restaurant_name} onChange={(v) => setF({ ...f, restaurant_name: v })} testId="add-tester-rest"/>
          <FieldInput label="موديل الجهاز" value={f.device_model} onChange={(v) => setF({ ...f, device_model: v })} placeholder="NyxPrinter / NB55 / OCOM" testId="add-tester-device"/>
          <FieldInput label="نسخة أندرويد" value={f.android_version} onChange={(v) => setF({ ...f, android_version: v })} placeholder="8.1" testId="add-tester-android"/>
          <div>
            <label className="text-xs font-bold block mb-1">ملاحظات (اختياري)</label>
            <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="w-full text-xs px-2 py-1.5 rounded-lg border border-[var(--border)] bg-white"
              data-testid="add-tester-notes"/>
          </div>
        </div>
        <button onClick={() => onSubmit(f)} disabled={disabled}
          className="w-full mt-4 py-2.5 rounded-xl bg-primary-brand text-white font-black text-sm hover:opacity-90 disabled:opacity-60"
          data-testid="add-tester-submit">
          {busy ? "جارٍ الحفظ..." : "إضافة"}
        </button>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, placeholder, required, testId }) {
  return (
    <div>
      <label className="text-xs font-bold block mb-1">{label}{required && <span className="text-red-500 mr-1">*</span>}</label>
      <input value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm px-3 py-2 rounded-lg border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none"
        data-testid={testId}/>
    </div>
  );
}
